
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import React from 'react';
import flush from 'just-flush';
import useTranslation from 'next-translate/useTranslation';
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from 'next';
import { useQuery } from 'urql';
import { LargeHero } from '@stereoagency/ui';
import { clientFactory, useFragment } from '@/gql';
import { findInArrayWaterfall, normalizeLocale, parseHtml, withPrepass } from '@/utils';
import { FindAllServiceSummariesQueryDocument, FindServiceDetailByIdQueryDocument, FindServiceSummariesForSlugOrIdQueryDocument, } from '@/gql/client/graphql';
import { LanguageFieldsFragment, MediaItemFieldsFragment, ServiceFieldsFragment, ServiceSummaryFieldsFragment, } from '@/gql/documents/fragments';
import { i18n } from '@/config';
import { ContentRenderer, Layout, SeoHead } from '@/components';
type UrlParams = {
    slugOrId: string;
};
type PageProps = {
    databaseId: number;
    preview?: boolean;
};
export default function Page({ databaseId, preview }: PageProps) {
    const { t } = useTranslation();
    const [{ data }] = useQuery({
        query: FindServiceDetailByIdQueryDocument,
        variables: { id: String(databaseId), asPreview: Boolean(preview) }
    });
    const fields = useFragment(ServiceFieldsFragment, data?.service);
    const summaryFields = useFragment(ServiceSummaryFieldsFragment, fields);
    const featuredImageFields = useFragment(MediaItemFieldsFragment, summaryFields?.featuredImage?.node);
    if (!data || !fields || !summaryFields)
        return null;
    return (<Layout preview={preview} theme={fields.pageCustomFields?.theme === 'dark' ? 'dark' : 'light'} enableLines={fields.pageCustomFields?.enableLines ?? false} useFixedNavbar={fields.pageCustomFields?.enableOverlayNavbar ?? false} hideFooterDivider={fields.pageCustomFields?.hideFooterDivider ?? false} hideSidetext={fields.pageCustomFields?.hideSidetext ?? false} sidetext={{
            text: fields.pageCustomFields?.sidetext?.text || summaryFields.cptServiceCustomFields?.category === 'service'
                ? t('global:services')
                : t('global:solutions'),
            placement: (fields.pageCustomFields?.sidetext?.placement ?? 'left') as 'left' | 'right'
        }}>
      <SeoHead seo={fields.seo ?? undefined}/>
      <LargeHero enableLines={fields.pageCustomFields?.enableLines ?? false} title={summaryFields.title ?? ''} content={summaryFields.excerpt ? parseHtml(summaryFields.excerpt) : undefined} videoUrl={summaryFields.cptServiceCustomFields?.videoBackgroundUrl ?? undefined} callToActions={[
            {
                // @TODO
                style: 'link',
                label: t('global:contact-us'),
                href: '/contact'
            },
        ]} image={featuredImageFields?.mediaItemUrl
            ? {
                alt: featuredImageFields.altText ?? '',
                src: featuredImageFields.mediaItemUrl,
                width: featuredImageFields.mediaDetails?.width ?? 1920,
                height: featuredImageFields.mediaDetails?.height ?? 1080
            }
            : undefined}/>
      <ContentRenderer enableLines={fields.pageCustomFields?.enableLines ?? false} html={fields.content ?? ''}/>
    </Layout>);
}
export const getStaticPaths: GetStaticPaths<UrlParams> = async () => {
    type PathsType = GetStaticPathsResult<UrlParams>['paths'];
    const client = clientFactory();
    const allServices = await client.query(FindAllServiceSummariesQueryDocument, {}).toPromise();
    const nodes = allServices.data?.services?.nodes ?? [];
    return {
        paths: flush(nodes.reduce<PathsType>((acc, node) => {
            const fields = useFragment(ServiceSummaryFieldsFragment, node);
            const language = useFragment(LanguageFieldsFragment, fields?.language);
            if (!fields?.slug)
                return acc;
            acc.push({
                locale: language?.code ? normalizeLocale(language?.code) : i18n.defaultLocale,
                params: { slugOrId: fields.slug! }
            });
            return acc;
        }, [])),
        fallback: 'blocking'
    };
};
const getStaticProps: GetStaticProps<PageProps, UrlParams> = async ({ preview, params, locale }) => {
    if (!params?.slugOrId) {
        return {
            notFound: true
        };
    }
    return withPrepass({
        params,
        preview,
        locale,
        propsFactory: async ({ urqlClient }) => {
            const queryParams = Number.isNaN(Number(params.slugOrId)) ? { slug: params.slugOrId } : { databaseId: Number(params.slugOrId) };
            const { data: servicesQuery, error } = await urqlClient.query(FindServiceSummariesForSlugOrIdQueryDocument, queryParams).toPromise();
            if (error) {
                throw error;
            }
            const services = useFragment(ServiceSummaryFieldsFragment, flush(servicesQuery?.services?.nodes ?? []));
            const service = findInArrayWaterfall(services, [locale ?? i18n.defaultLocale, i18n.defaultLocale], (entry, param) => {
                const language = useFragment(LanguageFieldsFragment, entry.language);
                return Boolean(language?.code && locale === normalizeLocale(param));
            });
            if (!service) {
                return {
                    notFound: true
                };
            }
            return {
                preview: !!preview,
                databaseId: service.databaseId
            };
        },
        Component: Page
    });
};

    async function __Next_Translate__getStaticProps__19633f993ad__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/service/[slugOrId]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__19633f993ad__ as getStaticProps }
  